import React, { useState, useEffect } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape from "cytoscape";
import cycola from "cytoscape-cola";
import cxtmenu from "cytoscape-cxtmenu"; // Cytoscape context menu extension
import FOUNDATION_CLASS from "./../../Utils/constants";
import DeleteConfirmationModal from "../common/DeleteConfirmationPopup";
import { deleteNode } from "../../services/schemaService";
import { toast } from "react-toastify";
import { Hoverbar } from "../common/HovrBar";

cytoscape.use(cycola);
cytoscape.use(cxtmenu);

const CytoscapeGraph = ({
  data,
  onCreateGroup,
  onCreateChild,
  onNode,
  onCyRef,
  refetch,
}) => {
  const [elements, setElements] = useState([]);
  const [cy, setCy] = useState(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [height, setHeight] = useState(window.innerHeight - 120);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    nodeData: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);

  const handleMouseOver = (event) => {
    const node = event.target;
    setHoveredNode(node.data());
  };

  const handleMouseOut = () => {
    setHoveredNode(null);
  };

  const toggleModal = () => {
    setDeleteModal((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  };

  const handleDeleteConfirm = async () => {
    setIsLoading(true);
    try {
      await deleteNode(deleteModal.nodeData.name);
      toast.success("delete class successfully");
      toggleModal();
      refetch();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete class");
    } finally {
      setIsLoading(false);
    }
  };

  // Resize the graph on window resize
  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight - 100);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (data) {
      const transformedNodes = buildFilteredTreeStructure(
        data.nodes,
        data.edges
      );
      const cytoscapeElements = convertToCytoscapeElements(
        transformedNodes,
        data.edges
      );
      setElements(cytoscapeElements);
    }
  }, [data]);

  const buildFilteredTreeStructure = (nodes, edges) => {
    const filteredNodes = nodes.filter((node) => node.class_kind === "node");

    const nodeMap = new Map(
      filteredNodes.map((node) => [
        node.id,
        {
          ...node,
          value: `${node.name.toLowerCase().replace(/\s+/g, "")}_${node.id}`,
          label: node.name,
          children: [],
        },
      ])
    );

    edges.forEach((edge) => {
      // Check if the source and target of the edge are the same
      if (edge.source !== edge.target) {
        const childNode = nodeMap.get(edge.source);
        const parentNode = nodeMap.get(edge.target);
        if (parentNode && childNode) {
          parentNode.children.push(childNode);
        }
      }
    });

    const rootNode = Array.from(nodeMap.values()).find(
      (node) => node.label === "Any"
    );

    const traverseNodes = (node) => {
      const result = [node];
      node.children.forEach((child) => result.push(...traverseNodes(child)));
      return result;
    };

    return rootNode ? traverseNodes(rootNode) : [];
  };

  const calculateNodePositions = (nodes) => {
    const positions = {};
    const initialX = 50; // Fixed X position for left alignment of the root node
    const rootNodeX = initialX; // Starting X position of root
    const centerY = window.innerHeight / 2; // Calculate the Y position to place the root node at the center vertically

    const positionNode = (node, currentX, currentY) => {
      positions[node.id] = { x: currentX, y: currentY };
      let childY = currentY - (node.children.length * 100) / 2; // Center the children around the parent node vertically

      node.children.forEach((child) => {
        positionNode(child, currentX + 200, childY); // Position the children to the right of the parent node
        childY += 100;
      });
    };

    nodes.forEach((node) => {
      if (!positions[node.id]) {
        positionNode(node, rootNodeX, centerY); // Start from the left with root node at the center vertically
      }
    });

    return positions;
  };

  const convertToCytoscapeElements = (nodes, edges) => {
    const nodePositions = calculateNodePositions(nodes);

    const cytoscapeNodes = nodes.map((n) => ({
      data: {
        id: n.id.toString(),
        label: n.name,
        colour: n.colour,
        ...n,
      },
      position: nodePositions[n.id],
      classes: n.labels ? n.labels.join(" ") : "",
    }));

    const cytoscapeEdges = edges
      .filter(
        (e) =>
          nodes.find((n) => n.id.toString() === e.source.toString()) &&
          nodes.find((n) => n.id.toString() === e.target.toString())
      )
      .map((e) => ({
        data: {
          source: e.source.toString(),
          target: e.target.toString(),
          label: e.label,
          ...e,
        },
        classes: e.label || "",
      }));

    return [...cytoscapeNodes, ...cytoscapeEdges];
  };

  const layout = {
    name: "dagre",
    avoidOverlap: true,
    rankDir: "RL", // Left to right (use "TB" for top to bottom)
    nodeSep: 40, // Spacing between nodes to avoid overlaps
    edgeSep: 10, // Spacing between edges
    rankSep: 150, // Vertical spacing between ranks (if using TB layout)
    padding: 5,
  };

  const stylesheet = [
    {
      selector: "node",
      style: {
        content: "data(label)",
        "border-style": "none",
        "border-width": "0px",
        "text-valign": "bottom",
        color: "black",
        "text-outline-width": 2,
        "background-color": "data(colour)",
        "text-outline-color": "#FFF",
        "font-size": "12px",
        "background-blacken": 0,
        "text-wrap": "wrap",
        "text-max-width": "100px",
      },
    },
    {
      selector: "edge",
      style: {
        content: "data(label)",
        "curve-style": "bezier",
        "target-arrow-shape": "triangle",
        "target-arrow-color": "#ccc",
        "line-color": "#ccc",
        width: 1.5,
        "font-size": "10px",
        "edge-text-rotation": "autorotate",
        "text-opacity": 1.0,
        opacity: 0.9,
      },
    },
  ];

  const mkCtxCommand = (ele, name, icon, enabled) => {
    return {
      content: `<span class="fa ${icon}"></span> ${name}`,
      select: (el, event) => {
        const nodeData = ele.data();
        if (name === "Design Group") {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
          // Wrap command execution in setTimeout to break event chain
          setTimeout(() => {
            onCreateGroup(nodeData);
          }, 0);
        } else if (name === "Create Child") {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
          // Wrap command execution in setTimeout to break event chain
          setTimeout(() => {
            onCreateChild(nodeData);
          }, 0);
        }
        if (name === "Delete Child") {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
          // Wrap command execution in setTimeout to break event chain
          setTimeout(() => {
            setDeleteModal({
              isOpen: true,
              nodeData: nodeData,
            });
          }, 0);
        }
      },
      disabled: !enabled,
      fillColor: "rgba(0,0,0," + (enabled ? "0.8" : "0.2") + ")",
    };
  };

  useEffect(() => {
    if (cy && elements.length > 0) {
      cy.layout(layout).run();
    }
  }, [cy, elements]);

  useEffect(() => {
    if (cy) {
      cy.container().addEventListener(
        "contextmenu",
        (e) => {
          e.preventDefault();
          return false;
        },
        false
      );

      cy.on("cxttapstart", (event) => {
        const originalEvent = event.originalEvent;
        if (originalEvent) {
          originalEvent.preventDefault();
          originalEvent.stopPropagation();
        }
      });
      cy.cxtmenu({
        selector: "node",
        commands: (ele) => {
          const nodeName = ele.data("label");
          let cmds = [];

          if (nodeName === "Group") {
            cmds.push(mkCtxCommand(ele, "Design Group", "fa-thumb-tack", true));
          } else {
            cmds.push(mkCtxCommand(ele, "Create Child", "fa-child", true));
          }

          if (FOUNDATION_CLASS.indexOf(ele.data().name) === -1) {
            cmds.push(mkCtxCommand(ele, "Delete Child", "fa-child", true));
          }

          return cmds;
        },
        // ... (other cxtmenu options)
      });

      cy.on("tap", "node", (event) => {
        const node = event.target;

        // Highlight the tapped node and fade others
        cy.batch(() => {
          cy.nodes()
            .not(node)
            .forEach((ele) => {
              ele.style({
                opacity: 0.2,
              });
            });

          cy.edges().forEach((ele) => {
            ele.style({
              opacity: 0.2,
            });
          });

          node.style({
            opacity: 1,
          });
        });

        onCyRef(cy);
        setTimeout(() => {
          onNode(node.data());
        }, 100);
      });

      // Handle click outside nodes (anywhere else on the canvas)
      cy.on("tap", (event) => {
        if (event.target === cy) {
          // Reset all styles
          cy.batch(() => {
            cy.nodes().forEach((ele) => {
              ele.style({
                opacity: 1,
              });
            });

            cy.edges().forEach((ele) => {
              ele.style({
                opacity: 1,
              });
            });
          });
        }
      });
    }
  }, [cy, onCreateGroup, onCreateChild, onNode]);

  return (
    <div style={{ height: height }}>
      <CytoscapeComponent
        elements={elements}
        style={{ width: "100%", height: "100%" }}
        cy={(cyInstance) => {
          setCy(cyInstance);
          onCyRef(cyInstance); // Pass the Cytoscape instance to the parent
          cyInstance.on("mouseover", "node", handleMouseOver);
          cyInstance.on("mouseout", "node", handleMouseOut);
        }}
        layout={layout}
        stylesheet={stylesheet}
        minZoom={0.1}
        maxZoom={2}
      />
      {contextMenuVisible && (
        <div
          className="context-menu"
          style={{ top: contextMenuPosition.y, left: contextMenuPosition.x }}
        >
          <ul>
            {/* <li onClick={() => onCreateGroup(selectedNode)}>Design Group</li>
            <li onClick={() => onCreateChild(selectedNode)}>Create Child</li> */}
          </ul>
        </div>
      )}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        toggle={toggleModal}
        onConfirm={handleDeleteConfirm}
        name={deleteModal.nodeData?.name || ""}
        isLoading={isLoading}
        deletionType={"node"}
      />
      {hoveredNode && <Hoverbar nodeData={hoveredNode} isInstance={false} />}
    </div>
  );
};

export default CytoscapeGraph;
